// Dashboard page component

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  Chip,
  Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

export default function DashboardPage() {
  const { user } = useAuth();

  return (
    <Box>
        {/* Welcome Message */}
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            Witaj, {user?.firstName}! 👋
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Oto przegląd Twojego magazynu biurowego
          </Typography>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
            }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <WarningIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                <Typography variant="h4" fontWeight="bold" mb={0.5}>
                  3
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Produkty krytyczne
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
            }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <MoneyIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                <Typography variant="h4" fontWeight="bold" mb={0.5}>
                  2,450 zł
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Wydatki w tym miesiącu
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #339af0 0%, #228be6 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
            }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <InventoryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                <Typography variant="h4" fontWeight="bold" mb={0.5}>
                  127
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Produktów w magazynie
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #ffd43b 0%, #fab005 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
            }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                <Typography variant="h4" fontWeight="bold" mb={0.5}>
                  5
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Zamówień w tym tygodniu
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Produkty wymagające uwagi
                </Typography>
                <Box>
                  {['Toner HP W2070A', 'Papier A4 Lenox', 'Długopisy Pentel'].map((product, index) => (
                    <Box 
                      key={product}
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between"
                      py={2}
                      borderBottom={index < 2 ? 1 : 0}
                      borderColor="divider"
                    >
                      <Box>
                        <Typography variant="body1">{product}</Typography>
                        <Typography variant="body2" color="error">
                          Stan: {2 + index}/10 (Krytyczny)
                        </Typography>
                      </Box>
                      <Button variant="contained" size="small">
                        Zamów
                      </Button>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Szybkie akcje
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<InventoryIcon />}
                  >
                    Dodaj produkt
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<ShoppingCartIcon />}
                  >
                    Nowe zamówienie
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<TrendingUpIcon />}
                  >
                    Raporty
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Demo Info */}
        <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Box
                component="img"
                src="https://biuropaczka.pl/static/version4fb3c9ef841dc2a9521b23425fcfa8773aff0c75/frontend/Snowdog/BiuroPaczka/pl_PL/images/bp-logo.svg"
                alt="BiuroPaczka Logo"
                sx={{
                  height: 28,
                  filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7500%) hue-rotate(324deg) brightness(108%) contrast(108%)'
                }}
              />
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Witaj w Stock Manager!
              </Typography>
            </Box>
            <Typography variant="body1">
              To jest wersja demonstracyjna systemu zarządzania magazynem biurowym. 
              Aplikacja zawiera 175 realnych produktów z BiuroPaczka.pl i pełny system autentykacji.
            </Typography>
            <Box mt={2}>
              <Typography variant="body2">
                <strong>Status implementacji:</strong>
              </Typography>
              <Typography variant="body2">
                ✅ System logowania • ✅ Dashboard • ✅ Zarządzanie produktami • ✅ Zamówienia • ✅ Raporty • ✅ Użytkownicy • ✅ Ustawienia
              </Typography>
            </Box>
          </CardContent>
        </Card>
    </Box>
  );
}